/**
 * PAD Snapshot View
 *
 * Read-only replica of the *original* Post-Assessment Discussion proforma after
 * a Lead clinician has redone it because the finalised Diagnostic Decision
 * disagreed with the outcome the original discussion was held under (SLaM
 * Path-1 redo flow). Data comes from
 * patients/{id}/padRedoArchive/{archiveId}.previousPadModule.
 *
 * Design choices (mirrors SSISnapshotView):
 * - Looks and navigates like the live PAD proforma (side navbar + question
 *   panels with the option "buttons" the clinician clicked and the clinician
 *   notes text boxes) so clinicians recognise the same form, not a summary.
 * - The artefact clinicians want here is the *proforma answers*, NOT the
 *   approved AI narrative. The AI outputs are still stored on the archive for
 *   audit, but this viewer deliberately does not show them.
 * - Everything is read-only: the component reads a single archive document and
 *   never touches the live patient record. It builds an in-memory "snapshot"
 *   patient model (archived PAD module injected over the live model) purely so
 *   the shared visibility helper (ShouldDisplayElement) resolves each panel the
 *   same way it did when the original discussion was held.
 *
 * Why we massage the snapshot model's outcome:
 * - Many PAD panels are gated by the ADHD outcome (positive-only strategies /
 *   treatment / goals). When the original Path-1 discussion was held there was
 *   no formal Diagnostic Decision yet, so those panels keyed off the
 *   *provisional* outcome. After the redo a (possibly different) formal DD now
 *   exists on the live record. To replay the archived form exactly as it stood,
 *   we force the snapshot model back to the archived provisional outcome and
 *   neutralise the live formal DD so ShouldDisplayElement falls back to it.
 */

import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { formatDateTime } from '../../../utils/timestampUtils';
import { ShouldDisplayElement } from '../../../utils/ShouldDisplayElement';
import { isPadAdhdOutcomePositive } from '../../../utils/conditionChecks';
import PADSideNavbar from './components/PADSideNavbar';
import DictationTextArea from '../../../components/DictationTextArea';
import InfoTooltip from '../../../components/InfoTooltip';
import { CheckIcon, PlusIcon } from '@heroicons/react/solid';
import {
  getPadStageOrder,
  getPadSidebarSections,
  buildPadSidebarUnlockFlags,
  rewriteSubsectionTitleDisplay,
  readIsCarryOutHealthChecksNow,
} from '../../../utils/padNavigationUtils';

const OUTCOME_LABEL = {
  positive: 'a positive ADHD outcome',
  negative: 'a negative (non-diagnosis) outcome',
};

const PAD_MODULE_KEY = '5_postAssessmentDiscussion';
const DD_MODULE_KEY = '4_diagnosticDecision';

/**
 * Amber banner making it unmistakable this is the archived original discussion,
 * superseded by a later redo. (The clinician approved this wording — it stays
 * visible at the top of the read-only proforma.)
 */
function PadSnapshotBanner({ archive }) {
  if (!archive) return null;

  // "held on" — prefer the first completion of the original discussion.
  const heldAt =
    formatDateTime(
      archive.completionMetadata?.firstCompletedAt ||
        archive.completionMetadata?.lastCompletedAt
    ) || '—';
  const revisedAt = formatDateTime(archive.archivedAt) || '—';
  const fromOutcome = OUTCOME_LABEL[archive.redoFromOutcome] || 'a different outcome';

  return (
    <div className="mx-8 mb-4 bg-amber-50 border border-amber-200 rounded-2xl p-4 text-left">
      <p className="text-sm font-semibold text-amber-900">
        Archived Post-Assessment Discussion (read-only)
      </p>
      <p className="text-sm text-amber-800 mt-1">
        A saved copy of the original discussion held with the patient on{' '}
        <strong>{heldAt}</strong>, completed for <strong>{fromOutcome}</strong>. Read-only.
      </p>
      <p className="text-xs text-amber-800 mt-2">
        A revised discussion was started on <strong>{revisedAt}</strong> after the diagnosis
        changed.
      </p>
    </div>
  );
}

/**
 * Read-only rendering of a single question exactly as it appeared on the live
 * PAD page: the question title (with any info tooltip) and its options as
 * pill "buttons", with the option(s) the clinician selected highlighted.
 */
function SnapshotQuestion({ question }) {
  if (!question) return null;

  const selected = Array.isArray(question.answer?.value) ? question.answer.value : [];
  const otherValue =
    typeof question.answer?.otherValue === 'string' ? question.answer.otherValue.trim() : '';
  const options = Array.isArray(question.options) ? question.options : [];

  return (
    <div className="flex items-start">
      <div className="w-96 flex items-center gap-2">
        {question.tooltipText ? (
          <InfoTooltip
            content={question.tooltipText}
            label={`Information about ${question.questionTitle}`}
            maxWidth="300px"
            className=""
          />
        ) : (
          <div className="w-4 h-4"></div>
        )}
        <h4 className="text-left font-semibold text-gray-700">{question.questionTitle}</h4>
      </div>

      <div className="ml-5">
        {options.length > 0 ? (
          <ul className="items-center flex flex-row flex-wrap gap-4">
            {options.map((option) => {
              const isSelected = selected.includes(option);
              return (
                <li
                  key={option}
                  className={`
                    flex-1 p-2 w-64 rounded-lg text-center
                    flex flex-row items-center justify-center gap-2
                    cursor-not-allowed opacity-75
                    ${isSelected
                      ? 'bg-violet-600 border-2 border-violet-800 font-semibold shadow-lg transform scale-105'
                      : 'bg-white border-2 border-dashed border-gray-300 shadow-sm'
                    }
                  `}
                >
                  <div className={isSelected ? 'text-white' : 'text-gray-400'}>
                    {isSelected ? (
                      <CheckIcon className="w-4 h-4" />
                    ) : (
                      <PlusIcon className="w-4 h-4" />
                    )}
                  </div>
                  <span
                    className={`font-semibold ${isSelected ? 'text-white' : 'text-gray-700'}`}
                  >
                    {option}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          // Questions with no option list (free-text answers) still show their
          // recorded value so nothing the clinician entered is hidden.
          selected.length > 0 && (
            <div className="w-96 p-3 border rounded-md bg-gray-100 text-gray-700 whitespace-pre-wrap">
              {selected.join(', ')}
            </div>
          )
        )}

        {/* Free-text "other" detail recorded alongside a selected option. */}
        {otherValue && (
          <p className="mt-2 text-sm text-gray-600 italic whitespace-pre-wrap">“{otherValue}”</p>
        )}
      </div>
    </div>
  );
}

/**
 * Renders every visible subsection of the active PAD section, each as the same
 * grey panel used on the live proforma — question rows plus (when present) the
 * clinician notes text box, shown read-only.
 */
function SnapshotSectionBody({ section, displayNumber, snapshotModel }) {
  const subsections = section?.subsections || {};

  // Sort subsections so 'prescribing' (named key) slots between
  // Cardiovascular ('3') and Outcome ('4'), matching 4_HLT.js render order.
  const orderedSubsections = useMemo(
    () =>
      Object.entries(subsections)
        .filter(([, sub]) => Boolean(sub))
        .sort(([a], [b]) => {
          const order = (k) => k === 'prescribing' ? 3.5 : (Number(k) || 99);
          return order(a) - order(b);
        }),
    [subsections]
  );

  const visibleSubsections = orderedSubsections.filter(([, sub]) =>
    ShouldDisplayElement(sub, snapshotModel)
  );

  if (visibleSubsections.length === 0) {
    return (
      <p className="text-sm text-gray-600 italic">
        No content was recorded for this section of the original discussion.
      </p>
    );
  }

  return (
    <>
      {visibleSubsections.map(([subsectionId, subsection], visibleIndex) => {
        const visibleQuestions = Object.entries(subsection.questions || {})
          .filter(([, question]) => Boolean(question))
          .filter(([, question]) => ShouldDisplayElement(question, snapshotModel))
          .sort(([a], [b]) => Number(a) - Number(b));

        // Compute display title dynamically from the visible index so
        // numbering stays correct regardless of which panels are present.
        const computedTitle = subsection.subsectionTitleDisplay
          ? rewriteSubsectionTitleDisplay(subsection.subsectionTitleDisplay, displayNumber)
          : `${displayNumber}.${visibleIndex + 1} ${subsection.subsectionTitle}`;

        return (
          <div
            key={subsectionId}
            className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200"
          >
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              {computedTitle}
            </h3>

            {subsection.tooltipText && (
              <p className="text-left text-gray-700 mb-4">{subsection.tooltipText}</p>
            )}

            <div className="space-y-6">
              {visibleQuestions.map(([questionId, question]) => (
                <SnapshotQuestion key={questionId} question={question} />
              ))}
            </div>

            {/* Clinician notes box (read-only) — same styled textarea as live. */}
            {subsection.hasClinicianNotesBox && (
              <div className="text-left mt-6">
                <h4 className="text-gray-700 text-sm font-semibold mb-2">
                  {subsection.textBoxSubtitle}
                </h4>
                <DictationTextArea
                  value={subsection.clinicianNotes || ''}
                  onChange={() => {}}
                  canEdit={false}
                />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

function SnapshotShell({ patientId, children }) {
  return (
    <Layout activePage="dashboard">
      <Breadcrumb
        tupleList={[
          ['Patient Overview', `/patient/${patientId}`],
          ['Original Post-Assessment Discussion'],
        ]}
      />
      {children}
    </Layout>
  );
}

function PADSnapshotView() {
  const { patientId, archiveId } = useParams();
  const { claims } = useAuth();
  const { patientDataModel: livePatientDataModel } = useContext(PatientDataContext);

  const [archive, setArchive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSectionKey, setActiveSectionKey] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const ref = doc(db, 'patients', patientId, 'padRedoArchive', archiveId);
        const snap = await getDoc(ref);
        if (cancelled) return;
        if (!snap.exists()) {
          setError('Original Post-Assessment Discussion record not found.');
          setArchive(null);
        } else {
          setArchive(snap.data());
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load PAD redo archive:', err);
          setError('Unable to load the original Post-Assessment Discussion record.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (patientId && archiveId) load();

    return () => {
      cancelled = true;
    };
  }, [patientId, archiveId]);

  // Build a patient model that looks like the live one but with the PAD module
  // replaced by the archived copy, and the outcome pinned to what the archived
  // discussion was held under (see file header for why). Config still comes
  // from the live patient doc (workflowConfig lives on the root, not the
  // module snapshot) so section order / AMP-on-off matches the real proforma.
  const snapshotModel = useMemo(() => {
    if (!livePatientDataModel || !archive?.previousPadModule) return null;

    const fromOutcome =
      archive.redoFromOutcome ||
      archive.previousProvisionalOutcome ||
      archive.previousPadCompletedOutcome ||
      null;

    // Neutralise the live formal DD so ShouldDisplayElement's diagnosisDecision
    // special-case falls back to the (archived) provisional outcome, exactly as
    // it did before any formal DD existed.
    const liveDd = livePatientDataModel.dataModel?.[DD_MODULE_KEY];
    const neutralisedDd = liveDd
      ? {
          ...liveDd,
          sections: Object.fromEntries(
            Object.entries(liveDd.sections || {}).map(([key, sectionValue]) => [
              key,
              { ...sectionValue, diagnosisDecision: '' },
            ])
          ),
        }
      : liveDd;

    const padModule = {
      ...archive.previousPadModule,
      ...(fromOutcome ? { provisionalAdhdOutcome: fromOutcome } : {}),
    };

    return {
      ...livePatientDataModel,
      workflowDecisions: {
        ...(livePatientDataModel.workflowDecisions || {}),
        // Force the provisional outcome "in force" so resolvers reflect the
        // outcome the archived discussion was actually held under.
        ciFinishPath: 'padInAppointment',
      },
      dataModel: {
        ...livePatientDataModel.dataModel,
        [PAD_MODULE_KEY]: padModule,
        ...(neutralisedDd ? { [DD_MODULE_KEY]: neutralisedDd } : {}),
      },
    };
  }, [livePatientDataModel, archive]);

  const wf = useWorkflowConfig(snapshotModel, claims);
  const isPadTreatmentFirst = wf?.isPadTreatmentFirst || false;
  const isAgreedMedicationPlanEnabled = wf?.isAgreedMedicationPlanEnabled || false;

  const archivedSections = snapshotModel?.dataModel?.[PAD_MODULE_KEY]?.sections || null;

  // Ordered clinician-facing stages, limited to sections that actually exist in
  // the archived module (drops AMP-off / TOC-shaped gaps so no phantom rows).
  const orderedStages = useMemo(() => {
    if (!archivedSections) return [];
    return getPadStageOrder({ isPadTreatmentFirst, isAgreedMedicationPlanEnabled }).filter(
      (stage) => archivedSections[stage.key]
    );
  }, [archivedSections, isPadTreatmentFirst, isAgreedMedicationPlanEnabled]);

  // Sidebar rows with the same unlock logic the clinician saw, computed against
  // the archived answers so a completed positive PAD unlocks its later stages.
  const sidebarSections = useMemo(() => {
    if (!archivedSections || orderedStages.length === 0) return [];
    const isBeginningMedicationNow = Boolean(
      readIsCarryOutHealthChecksNow(snapshotModel) &&
        archivedSections?.['4']?.subsections?.['4']?.questions?.['2']?.answer?.value?.includes(
          'Yes'
        )
    );
    const unlockFlags = buildPadSidebarUnlockFlags({
      patientDataModel: snapshotModel,
      isPadTreatmentFirst,
      isDiagnosisADHD: isPadAdhdOutcomePositive(snapshotModel),
      isCarryOutHealthChecksNow: readIsCarryOutHealthChecksNow(snapshotModel),
      isBeginningMedicationNow,
      section1Complete: true,
    });
    return getPadSidebarSections(archivedSections, orderedStages, unlockFlags);
  }, [archivedSections, orderedStages, snapshotModel, isPadTreatmentFirst]);

  // Default to the first stage once the sidebar is known. Use a functional
  // update so a clinician click that arrives before this effect runs is NOT
  // overwritten by a stale "still null" closure — under CI load that race
  // left the view stuck on Review after clicking Strategies.
  useEffect(() => {
    if (orderedStages.length === 0) return;
    setActiveSectionKey((current) => current ?? orderedStages[0].key);
  }, [orderedStages]);

  if (loading || error) {
    return (
      <SnapshotShell patientId={patientId}>
        <div className="flex-grow px-8 pb-12 text-left">
          {loading && (
            <p className="text-sm text-gray-600">
              Loading original Post-Assessment Discussion record...
            </p>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
      </SnapshotShell>
    );
  }

  // Archive exists but has no proforma snapshot (e.g. an older archive created
  // before the full PAD module was captured). Nothing proforma-like to show.
  if (!snapshotModel || !archivedSections) {
    return (
      <SnapshotShell patientId={patientId}>
        <PadSnapshotBanner archive={archive} />
        <div className="px-8 pb-12 text-left">
          <p className="text-sm text-gray-600 italic">
            The original discussion proforma is not available for this record.
          </p>
        </div>
      </SnapshotShell>
    );
  }

  const activeIndex = Math.max(
    0,
    sidebarSections.findIndex((s) => s.key === activeSectionKey)
  );
  const activeSidebarSection = sidebarSections[activeIndex];
  const activeSection = activeSidebarSection ? archivedSections[activeSidebarSection.key] : null;

  const navigateToSection = (index) => {
    const target = sidebarSections[index];
    if (target && target.isUnlocked) {
      setActiveSectionKey(target.key);
      // Scroll is best-effort — jsdom (and some browsers) may not implement
      // Element.scrollTo, and a throw here must never undo the section switch.
      try {
        const mainContentArea = document.querySelector('.flex-1.overflow-y-auto');
        if (mainContentArea?.scrollTo) {
          mainContentArea.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (typeof window.scrollTo === 'function') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch {
        // ignore — navigation already applied
      }
    }
  };

  return (
    <SnapshotShell patientId={patientId}>
      <PadSnapshotBanner archive={archive} />
      <div className="px-8 mb-2">
        <Link
          to={`/patient/${patientId}`}
          className="text-sm font-semibold text-violet-700 hover:text-violet-900 underline"
        >
          Back to patient overview
        </Link>
      </div>
      <div className="flex">
        <PADSideNavbar
          sections={sidebarSections}
          currentSectionIndex={activeIndex}
          navigateToSection={navigateToSection}
        />
        <div className="flex-grow p-8">
          {activeSidebarSection && (
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">
                {activeSidebarSection.sectionNumber}. {activeSidebarSection.sectionTitle}
              </h2>
            </div>
          )}

          {activeSection ? (
            <SnapshotSectionBody
              section={activeSection}
              displayNumber={activeSidebarSection?.sectionNumber}
              snapshotModel={snapshotModel}
            />
          ) : (
            <p className="text-sm text-gray-600 italic">
              No content was recorded for this section of the original discussion.
            </p>
          )}
        </div>
      </div>
    </SnapshotShell>
  );
}

export default PADSnapshotView;
